import { NOTE_ACTION } from '@constants/notesAttachment';
import { SnackbarApiErrorMessage } from '@ui-framework/index';
import axios from 'axios';

const getNotes = async ({ params, page, limit }) => {
  const result = await params?.get(
    `${params.getApiUrl()}/sxcl/v1/service-jobs/${params?.serviceJobId}/notes?page=${page}&limit=${limit}`,
  );
  return result;
};

const deleteNote = async ({ params, id }) => {
  const response = await params?.remove(
    `${params?.getApiUrl()}/sxcl/v1/service-jobs/${params?.serviceJobId}/notes/${id}`,
  );
  if (response?.ok) {
    return true;
  }
  params.snackbar.showMessage({
    type: 'error',
    data: <SnackbarApiErrorMessage errors={response?.errors} />,
  });
  return false;
};

const saveNote = async ({
  params,
  id = null,
  serviceJobId,
  note,
  action = '',
}) => {
  if (action === NOTE_ACTION.ADD) {
    const apiUrl = `${params?.getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/notes`;
    const result = await params?.post(apiUrl, { note });
    return result;
  }

  const apiUrl = `${params?.getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/notes/${id}`;
  const result = await params?.put(apiUrl, { note });
  return result;
};

const fetchAttachmentForNote = async ({
  params,
  serviceJobId,
  id,
  page = 1,
  limit,
}) => {
  const apiUrl = `${params?.getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/notes/${
    id
  }/attachments?page=${page}&limit=${limit}`;
  const response = await params?.get(apiUrl);
  return response;
};

const getDownloadUrl = async ({
  params,
  serviceJobId,
  noteId,
  attachmentid,
}) => {
  const apiUrl = `${params.getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/notes/${noteId}/attachments/download?attachmentId=${attachmentid}`;
  const response = await params?.get(apiUrl);
  return response;
};

const deleteAttachment = async ({ params, serviceJobId, noteId, fileId }) => {
  const response = params?.remove(
    `${params?.getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/notes/${
      noteId
    }/attachments/${fileId}`,
  );
  return response;
};

const getPresignedUrl = async ({
  params,
  serviceJobId,
  noteId,
  fileName,
  attachmentType,
  description,
  fileSize,
}) => {
  const url = `${params?.getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/notes/${noteId}/attachments/upload`;
  const response = await params?.post(url, {
    attachmentType,
    description,
    fileName,
    fileSize,
  });
  return response;
};

const acknowledgeUpload = async ({
  params,
  noteId,
  attachmentId,
  serviceJobId,
}) => {
  const url = `${params?.getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/notes/${noteId}/attachments/confirm-upload?attachmentId=${attachmentId}`;
  const response = await params?.post(url);
  return response;
};

export const withInjectedErrorPath = (response, pathName) => {
  return {
    ...response,
    errors: Array.isArray(response.errors)
      ? response.errors.map(error => ({
          ...error,
          path: pathName,
        }))
      : response.errors,
  };
};

const uploadPrivateDocument = async ({
  params,
  serviceJobId,
  noteId,
  fileName,
  file,
  attachmentType,
  description,
  fileSize,
}) => {
  const pathToInject = fileName || '';
  try {
    const awsPresignUrlResponse = await getPresignedUrl({
      params,
      noteId,
      serviceJobId,
      fileName,
      attachmentType,
      description,
      fileSize,
    });

    if (awsPresignUrlResponse?.ok) {
      await axios({
        method: 'put',
        url: awsPresignUrlResponse?.data?.presignedUrl,
        data: file,
        headers: {
          'Content-Type': 'application/octet-stream',
        },
      });

      const acknowledgeResponse = await acknowledgeUpload({
        params,
        serviceJobId,
        noteId,
        attachmentId: awsPresignUrlResponse?.data?.attachmentId,
      });

      if (acknowledgeResponse?.ok) {
        return Promise.resolve({
          ok: true,
          data: { attachmentId: awsPresignUrlResponse.attachmentId },
          errors: null,
        });
      }
      return Promise.resolve(
        withInjectedErrorPath(acknowledgeResponse, pathToInject),
      );
    }
    return Promise.resolve(
      withInjectedErrorPath(awsPresignUrlResponse, pathToInject),
    );
  } catch (err) {
    return Promise.resolve({
      ok: false,
      errors: [{ message: err.message, path: pathToInject }],
    });
  }
};

export {
  uploadPrivateDocument,
  deleteNote,
  getNotes,
  saveNote,
  fetchAttachmentForNote,
  getDownloadUrl,
  deleteAttachment,
};
